import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { UserAppService } from 'src/app/services/user-app.service';

@Component({
  selector: 'app-add-users',
  template: `
    <div class="create-user-page">
      <!-- Page header -->
      <a class="cu-back" routerLink="../manage-users">
        <span aria-hidden="true">&larr;</span> Back to users
      </a>
      <h1 class="cu-title">Create user</h1>
      <p class="cu-subtitle">Add a teammate and set their access.</p>

      <!-- Form card -->
      <div class="panel">
        <form [formGroup]="registrationForm" (ngSubmit)="onSubmit()">
          <!-- First / Last name side-by-side on wider widths -->
          <div class="cu-row2">
            <div class="cu-field">
              <label class="cu-label" for="cu-firstName">First name</label>
              <mat-form-field class="cu-mat" appearance="fill">
                <input
                  id="cu-firstName"
                  matInput
                  formControlName="firstName"
                  required
                />
              </mat-form-field>
            </div>

            <div class="cu-field">
              <label class="cu-label" for="cu-lastName">Last name</label>
              <mat-form-field class="cu-mat" appearance="fill">
                <input
                  id="cu-lastName"
                  matInput
                  formControlName="lastName"
                  required
                />
              </mat-form-field>
            </div>
          </div>

          <div class="cu-field">
            <label class="cu-label" for="cu-email">Email</label>
            <mat-form-field class="cu-mat" appearance="fill">
              <input
                id="cu-email"
                matInput
                placeholder="pat@example.com"
                formControlName="email"
                required
              />
            </mat-form-field>
          </div>

          <div class="cu-field">
            <label class="cu-label">Roles</label>
            <mat-form-field class="cu-mat" appearance="fill">
              <mat-select
                multiple
                formControlName="role"
                placeholder="Select roles"
              >
                <mat-option
                  [value]="role"
                  *ngFor="let role of availableUserRoles"
                  >{{ role }}</mat-option
                >
              </mat-select>
            </mat-form-field>
          </div>

          <div class="cu-field">
            <label class="cu-label" for="cu-password">Password</label>
            <mat-form-field class="cu-mat" appearance="fill">
              <input
                id="cu-password"
                matInput
                formControlName="password"
                required
                [type]="pwVisible.password ? 'text' : 'password'"
              />
              <button
                type="button"
                matSuffix
                class="cu-eye"
                tabindex="-1"
                (click)="pwVisible.password = !pwVisible.password"
                [attr.aria-label]="
                  pwVisible.password ? 'Hide password' : 'Show password'
                "
              >
                <mat-icon>{{
                  pwVisible.password ? 'visibility_off' : 'visibility'
                }}</mat-icon>
              </button>
            </mat-form-field>
            <p class="cu-help">At least 8 characters.</p>
          </div>

          <div class="cu-field">
            <label class="cu-label" for="cu-confirmPassword"
              >Confirm password</label
            >
            <mat-form-field class="cu-mat" appearance="fill">
              <input
                id="cu-confirmPassword"
                matInput
                formControlName="confirmPassword"
                required
                [type]="pwVisible.confirm ? 'text' : 'password'"
              />
              <button
                type="button"
                matSuffix
                class="cu-eye"
                tabindex="-1"
                (click)="pwVisible.confirm = !pwVisible.confirm"
                [attr.aria-label]="
                  pwVisible.confirm ? 'Hide password' : 'Show password'
                "
              >
                <mat-icon>{{
                  pwVisible.confirm ? 'visibility_off' : 'visibility'
                }}</mat-icon>
              </button>
              <mat-error
                *ngIf="
                  registrationForm.get('confirmPassword').errors
                    ?.passwordMismatch
                "
              >
                Passwords do not match.</mat-error
              >
            </mat-form-field>
          </div>

          <div class="cu-actions">
            <a class="cu-btn-ghost" routerLink="../manage-users">Cancel</a>
            <button
              type="submit"
              class="cu-btn-primary"
              [disabled]="registrationForm.invalid"
            >
              Create user
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
  styleUrls: ['./add-users.component.scss'],
})
export class AddUsersComponent implements OnInit {
  registrationForm: FormGroup | any;

  pwVisible = { password: false, confirm: false };

  availableUserRoles = ['customer_consultant', 'sales_rep'];

  constructor(
    private formBuilder: FormBuilder,
    private userServices: UserAppService,
    private snackBar: SharedFeaturesService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.registrationForm = this.formBuilder.group(
      {
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        role: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required],
      },
      {
        validator: this.passwordMatchValidator,
      }
    );
    this.userServices.getUserRoles().subscribe({
      next: (response:any) => {
        this.availableUserRoles = response?.message;
      },
      error: (error: any) => {
        console.error('An error occurred ', error);
      },
    })
  }

  onSubmit(): void {
    if (this.registrationForm.valid) {
      console.debug('sending values', this.registrationForm.value);
      this.userServices.addUser(this.registrationForm.value).subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(
            response?.msg,
            'Close',
            'success-snackbar'
          );
          this.router.navigate(['../manage-users'], { relativeTo: this.route });
        },
        error: (error: any) => {
          this.snackBar.openSnackBar(
            error?.error?.msg || error?.msg || error?.message,
            'Close',
            'error-snackbar'
          );
        },
      });
    }
  }

  passwordMatchValidator(formGroup: FormGroup) {
    const passwordControl = formGroup.get('password');
    const confirmPasswordControl = formGroup.get('confirmPassword');
    if (passwordControl?.value != confirmPasswordControl?.value) {
      confirmPasswordControl?.setErrors({ passwordMismatch: true });
    } else {
      confirmPasswordControl?.setErrors(null);
    }
  }
}
